import type { LearnTopic } from '../types';
import { $, width } from '../format';

export const spreadsTopic: LearnTopic = {
  id: 'spreads',
  tier: 'basics',
  title: 'Spreads: two legs, a known risk',
  summary:
    'Two options of the same type and expiration at different strikes. The bought leg turns an open-ended risk into a number you can look up before you trade.',
  body: [
    'A vertical spread is two options of the same type and the same expiration at different strikes: one sold, one bought. It is called vertical because the two strikes sit above each other in the same column of the option chain.',
    'The bought leg is the point of the whole structure. On its own, a sold option has a loss that keeps growing as the stock moves against it. Put a bought option beyond it and the two move together past that strike — one loses, the other gains — so the loss stops. What it stops at is the distance between the strikes, and that distance is the width.',
    'Four numbers describe any vertical, and everything else is commentary: the credit or debit, the width, the most it can lose, and the most it can make. Sell a spread for a credit and the credit is the most you can make, while the width less that credit is the most you can lose. Buy a spread for a debit and it is the other way round: what you paid is the most you can lose, and the width less what you paid is the most you can make.',
    'Where you put the strikes is the only dial. Closer to the current price pays more and is more likely to be touched; further away pays less and is safer. You sell your short strikes at 10 to 15 delta — roughly a 10 to 15 percent chance of finishing in the money — and take the smaller credit that comes with them.',
    "A spread is also what makes a small account able to hold many positions. It turns what would be a large share commitment into a small, bounded one, and bounded positions can be counted. Read the most it can lose and the most it can make off the platform before you send the order — both numbers are knowable in advance, so there is no excuse for being surprised by either. The buyers-and-sellers examples and the lessons behind them are Howard's.",
  ],
  explain(ctx) {
    const { shortPut, longPut, shortCall, longCall, credit } = ctx;

    const side = (name: 'put' | 'call', short: number | null, long: number | null) => {
      if (short == null && long == null) return `There is no ${name} spread on this position.`;
      if (short == null || long == null) {
        const lone = short ?? long;
        return `Your ${name} side is a lone ${lone} ${name}, not a spread: there is no second strike to cap it.`;
      }
      // Strikes read low to high on both sides, the way they sit on the chain.
      const low = Math.min(short, long);
      const high = Math.max(short, long);
      const spreadWidth = width(short, long);
      return credit != null
        ? `Your ${name} spread is the ${low}/${high}: ${spreadWidth} wide, and the most it can lose is ${$(spreadWidth - credit)} a share.`
        : `Your ${name} spread is the ${low}/${high}: ${spreadWidth} wide. With no credit recorded, treat the full ${spreadWidth} a share as the worst case.`;
    };

    return [side('put', shortPut, longPut), side('call', shortCall, longCall)];
  },
  appliesTo() {
    return true;
  },
};
